import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { FormField } from '@/components/shared/form-field';
import { SignInShell } from '@/components/sign-in-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OtpInput } from '@/components/ui/otp-input';
import { PasswordInput } from '@/components/ui/password-input';
import { Spinner } from '@/components/ui/spinner';
import { AUTH_CLIENT_SOURCE } from '@/lib/auth-client-source';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { adoptSession, type SessionPayload } from '@/lib/session-adopt';
import { QrLoginPanel } from '@/pages/qr-login-panel';
import { useAuthStore } from '@/stores/auth-store';

export const SignInPage = () => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const location = useLocation();
  const status = useAuthStore((state) => state.status);
  const restore = useAuthStore((state) => state.restore);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  const signIn = useMutation({
    mutationFn: (override?: { totpCode?: string }) => {
      const code = override?.totpCode ?? totpCode;
      return api.post<SessionPayload>('/auth/login', {
        email,
        password,
        source: AUTH_CLIENT_SOURCE,
        ...(needsTotp && code ? { totpCode: code } : {}),
      });
    },
    onSuccess: (payload) => {
      const user = adoptSession(payload);
      if (user.platformRole !== 'platform_admin' && user.platformRole !== 'platform_manager') {
        setFormError(t('signIn.accessDenied'));
        return;
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
        setTotpCode('');
        setFormError(null);
        return;
      }
      if (needsTotp) {
        setTotpCode('');
      }
      setFormError(
        error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'),
      );
    },
  });

  const submitSignIn = (override?: { totpCode?: string }) => {
    setFormError(null);
    signIn.mutate(override);
  };

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/sign-in' ? from : '/churches'} replace />;
  }

  if (status === 'unknown') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const from = (location.state as { from?: string } | null)?.from;
  const destination = from && from !== '/sign-in' ? from : '/churches';

  return (
    <SignInShell
      showQr={!needsTotp}
      qr={<QrLoginPanel destination={destination} />}
      form={
        <>
          <div className="mb-6 space-y-1">
            <h1 className="text-section-title font-semibold text-fg">{t('signIn.title')}</h1>
            <p className="text-body-sm text-fg-secondary">{t('signIn.description')}</p>
          </div>

          <form
            className="space-y-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (needsTotp) {
                if (totpCode.length === 6) submitSignIn({ totpCode });
                return;
              }
              submitSignIn();
            }}
          >
            {!needsTotp ? (
              <>
                <FormField label={t('signIn.email')} required>
                  {(field) => (
                    <Input
                      {...field}
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  )}
                </FormField>
                <FormField label={t('signIn.password')} required>
                  {(field) => (
                    <PasswordInput
                      {...field}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  )}
                </FormField>
              </>
            ) : (
              <FormField label={t('signIn.totpLabel')} hint={t('signIn.totpHelp')} required>
                {(field) => (
                  <OtpInput
                    {...field}
                    value={totpCode}
                    onChange={setTotpCode}
                    autoFocus
                    disabled={signIn.isPending}
                    invalid={Boolean(formError)}
                    aria-label={t('signIn.totpLabel')}
                    onComplete={(code) => {
                      if (!signIn.isPending) submitSignIn({ totpCode: code });
                    }}
                  />
                )}
              </FormField>
            )}

            {formError ? (
              <p className="text-body-sm text-danger" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                loading={signIn.isPending}
                disabled={needsTotp && totpCode.length !== 6}
              >
                {t('signIn.submit')}
              </Button>
            </div>
          </form>
        </>
      }
    />
  );
};
